import { useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer, PageHero } from "../components/SharedComponents";
import {
  FaCheck, FaArrowRight, FaShieldAlt, FaChartLine, FaDollarSign, FaUsers,
  FaChevronDown, FaChevronUp, FaIndustry, FaHandshake, FaSearchDollar,
  FaBalanceScale, FaRegCalendarCheck, FaLightbulb, FaStar, FaRegClock,
  FaTrophy, FaMapMarkerAlt, FaCalculator, FaFileSignature, FaUserTie,
  FaBullseye, FaWarehouse, FaBoxOpen, FaSyncAlt, FaChartBar, FaTruck,
  FaUsers as FaTeam, FaLock, FaRocket
} from "react-icons/fa";

/* ==========================================
   SHARED SUB-COMPONENTS
   ========================================== */

const StatsBar = ({ stats }) => (
  <div className="svc-stats-bar">
    {stats.map((s, i) => (
      <div key={i} className="svc-stat-item">
        <div className="svc-stat-num">{s.num}</div>
        <div className="svc-stat-label">{s.label}</div>
      </div>
    ))}
  </div>
);

const FAQSection = ({ faqs }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="svc-faq">
      <h3>Frequently Asked Questions</h3>
      <div className="faq-list">
        {faqs.map((faq, i) => (
          <div key={i} className={`faq-item ${open === i ? "open" : ""}`}>
            <button className="faq-question" onClick={() => setOpen(open === i ? null : i)}>
              <span>{faq.q}</span>
              {open === i ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            {open === i && (
              <div className="faq-answer">
                <p>{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const IndustriesRow = ({ industries }) => (
  <div className="svc-industries">
    <h3>Industries We Serve</h3>
    <div className="industries-grid">
      {industries.map((ind, i) => (
        <div key={i} className="industry-pill">
          <span className="industry-icon">{ind.icon}</span>
          <span>{ind.label}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ==========================================
   SERVICE DATA
   ========================================== */

const serviceData = {

  /* ---- BUSINESS ACQUISITIONS ---- */
  "business-acquisitions": {
    title: "Business Acquisitions",
    heading: "Strategic Business Acquisition Advisory",
    intro: "United Taxpert provides comprehensive financial due diligence and advisory services for businesses looking to acquire, merge, or expand. Our experts guide you through every stage of the acquisition process — from initial valuation to post-merger integration — ensuring your transaction is financially sound and strategically positioned for success.",
    stats: [
      { num: "10x", label: "Value Proposition" },
      { num: "98%", label: "Client Satisfaction Rate" },
      { num: "45", label: "Avg. Days to Close" },
      { num: "25+", label: "Years M&A Experience" }
    ],
    includes: [
      [
        "Financial Due Diligence",
        "Business Valuation Analysis",
        "Deal Structuring & Advisory",
        "Tax Implications Analysis",
        "LOI & Purchase Agreement Review"
      ],
      [
        "Post-Acquisition Integration Support",
        "Risk Assessment & Mitigation",
        "Cash Flow & Synergy Modeling",
        "Working Capital Analysis",
        "Exit Strategy Planning"
      ]
    ],
    benefits: [
      { icon: <FaShieldAlt size={28}/>, title: "Risk Management", desc: "Comprehensive due diligence protects you from hidden liabilities, undisclosed debts, and financial surprises post-close.", blue: true },
      { icon: <FaDollarSign size={28}/>, title: "Value Maximization", desc: "Strategic deal structuring ensures you pay the right price and capture maximum value from every transaction.", blue: false },
      { icon: <FaChartLine size={28}/>, title: "Growth Strategy Alignment", desc: "Expert guidance aligns every acquisition with your long-term business growth objectives and financial goals.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Expert Partnership", desc: "Our experienced M&A team walks with you through every step of the complex acquisition process start to finish.", blue: false }
    ],
    /* Fresh Idea: Deal Timeline */
    freshSection: {
      type: "timeline",
      title: "Our Proven Acquisition Process",
      subtitle: "A structured, 5-stage approach that protects your investment and ensures transaction success.",
      steps: [
        { icon: <FaFileSignature size={22}/>, label: "Letter of Intent", desc: "We help draft and review the LOI to set clear terms and protect your position from day one." },
        { icon: <FaSearchDollar size={22}/>, label: "Due Diligence", desc: "Deep-dive financial, legal, and operational review to uncover risks before you commit." },
        { icon: <FaBalanceScale size={22}/>, label: "Valuation", desc: "Independent business valuation using multiple methods to determine true fair market value." },
        { icon: <FaHandshake size={22}/>, label: "Negotiation", desc: "Strategic negotiation support to optimize deal terms, price, and conditions in your favor." },
        { icon: <FaTrophy size={22}/>, label: "Closing & Integration", desc: "Seamless closing coordination and a 90-day post-merger integration support plan." }
      ]
    },
    risks: [
      { title: "Poor Due Diligence", desc: "73% of acquisitions fail due to inadequate due diligence. We leave nothing unchecked.", icon: <FaShieldAlt /> },
      { title: "Overvaluation", desc: "Overpaying is the #1 destroyer of acquisition ROI. Our independent valuation protects you.", icon: <FaDollarSign /> },
      { title: "Tax Missteps", desc: "Improper deal structure can cost millions in unnecessary taxes. We optimize from the start.", icon: <FaCalculator /> },
      { title: "Integration Failure", desc: "Post-merger integration is where deals often fall apart. Our 90-day plan ensures smooth transition.", icon: <FaRocket /> }
    ],
    industries: [
      { icon: "🏥", label: "Healthcare" }, { icon: "🏗️", label: "Construction" },
      { icon: "🍽️", label: "Food & Beverage" }, { icon: "💻", label: "Technology" },
      { icon: "🏪", label: "Retail" }, { icon: "🏭", label: "Manufacturing" },
      { icon: "🏨", label: "Hospitality" }, { icon: "⚖️", label: "Professional Services" }
    ],
    faqs: [
      { q: "How long does a typical business acquisition take?", a: "The timeline varies based on deal complexity, but most acquisitions take 45–120 days from LOI signing to close. Factors like due diligence scope, financing arrangements, and regulatory approvals can extend this timeline. Our team works to keep your deal moving efficiently." },
      { q: "What is included in financial due diligence?", a: "Our due diligence covers 3–5 years of financial statements, tax returns, accounts receivable/payable aging, payroll records, contracts, inventory valuation, off-balance sheet liabilities, and an assessment of revenue quality and sustainability." },
      { q: "Can you help with acquisition financing?", a: "Yes. We work alongside your lenders and advisors to prepare the financial packages and projections needed for SBA loans, conventional business loans, seller financing arrangements, and private equity partnerships." },
      { q: "Do you provide post-acquisition support?", a: "Absolutely. We offer a structured 90-day post-merger integration program covering financial system consolidation, chart of accounts unification, payroll integration, and ongoing bookkeeping and financial reporting for the newly combined entity." }
    ]
  },

  /* ---- TAX PLANNING ---- */
  "tax-planning": {
    title: "Tax Planning",
    heading: "Strategic Tax Planning & Year-Round Advisory",
    intro: "At United Taxpert, tax planning isn't a once-a-year event — it's an ongoing strategy. Our certified tax professionals analyze your full financial picture year-round to identify savings opportunities, minimize liabilities, and keep your business 100% compliant with evolving federal and state tax laws.",
    stats: [
      { num: "99.9%", label: "Accuracy Rate" },
      { num: "✓", label: "Handled Complex Tax Returns" },
      { num: "↑", label: "Delivered Bigger Refunds Than Expected" },
      { num: "25+", label: "Years of Tax Expertise" }
    ],
    includes: [
      [
        "Individual & Business Tax Returns",
        "Federal & State Tax Compliance",
        "Quarterly Estimated Tax Payments",
        "Tax Deduction Optimization",
        "Business Entity Structure Advisory"
      ],
      [
        "IRS Audit Representation",
        "Tax Credit Identification & Filing",
        "Multi-State & Local Tax Filing",
        "Retirement & Estate Tax Planning",
        "Year-Round Tax Strategy Updates"
      ]
    ],
    benefits: [
      { icon: <FaShieldAlt size={28}/>, title: "Full Tax Compliance", desc: "Stay compliant with ever-changing federal and state tax laws with expert guidance and proactive monitoring.", blue: true },
      { icon: <FaChartLine size={28}/>, title: "Maximize Deductions", desc: "Identify every eligible deduction and tax credit to legally and significantly reduce your total tax burden.", blue: false },
      { icon: <FaDollarSign size={28}/>, title: "Long-Term Tax Savings", desc: "Multi-year strategic planning that builds lasting tax advantages and financial benefits for your business.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Personalized Advisory", desc: "Dedicated tax advisors who know your unique situation, industry, and goals inside and out year-round.", blue: false }
    ],
    /* Fresh Idea: Proactive vs Reactive Comparison */
    freshSection: {
      type: "comparison",
      title: "Proactive Tax Planning vs. Reactive Tax Filing",
      subtitle: "Most businesses only think about taxes at year-end. We change that.",
      left: {
        heading: "❌ Reactive (Filing Only)",
        style: "reactive",
        points: [
          "Pay whatever taxes come due",
          "Miss deductions and credits",
          "Scramble every April",
          "No strategy, just compliance",
          "Overpay year after year"
        ]
      },
      right: {
        heading: "✅ Proactive (United Taxpert)",
        style: "proactive",
        points: [
          "Year-round tax minimization strategy",
          "Every deduction identified & captured",
          "No surprises — ever",
          "Tax-efficient business decisions",
          "Measurable savings, every year"
        ]
      }
    },
    taxCalendar: [
      { date: "Jan 15", desc: "Q4 Estimated Tax Payment Due" },
      { date: "Apr 15", desc: "Individual & Corporate Tax Return Deadline" },
      { date: "Jun 16", desc: "Q2 Estimated Tax Payment Due" },
      { date: "Sep 15", desc: "Q3 Estimated Tax Payment Due" },
      { date: "Oct 15", desc: "Extended Return Deadline" },
      { date: "Dec 31", desc: "Year-End Tax Planning Deadline" }
    ],
    industries: [
      { icon: "👩‍💼", label: "Self-Employed" }, { icon: "🏪", label: "Small Business" },
      { icon: "🏢", label: "Corporations" }, { icon: "🏠", label: "Real Estate" },
      { icon: "💼", label: "Partnerships" }, { icon: "🌍", label: "International" },
      { icon: "👨‍⚕️", label: "Medical Practices" }, { icon: "⚖️", label: "Law Firms" }
    ],
    faqs: [
      { q: "When should I start tax planning for my business?", a: "Tax planning should be a year-round activity, not just something you do in April. The best time to start is now. We recommend quarterly strategy sessions to assess your tax position, adjust withholdings, make strategic purchases, and implement retirement contributions that reduce your taxable income." },
      { q: "Can you help if I'm behind on filing tax returns?", a: "Yes. We regularly help clients catch up on unfiled returns, negotiate IRS installment agreements, apply for penalty abatements, and resolve back-tax issues. We handle all IRS correspondence on your behalf." },
      { q: "What's the difference between tax preparation and tax planning?", a: "Tax preparation is backward-looking — recording what happened. Tax planning is forward-looking — strategically positioning your finances to minimize future tax liability. United Taxpert provides both, giving you the best of reactive compliance and proactive strategy." },
      { q: "Do you handle multi-state tax filings?", a: "Yes. With increasing remote workforces and interstate commerce, multi-state tax compliance is critical. We handle nexus analysis, apportionment calculations, and state-specific returns across all 50 states to ensure full compliance." }
    ]
  },

  /* ---- PAYROLL SERVICES ---- */
  "payroll-services": {
    title: "Payroll Services",
    heading: "Accurate, Compliant & On-Time Payroll Management",
    intro: "United Taxpert delivers seamless payroll processing that ensures your employees are paid accurately and on time, every time. We handle everything — from tax withholdings and direct deposits to W-2/1099 filings and multi-state compliance — so you can focus on growing your business, not managing spreadsheets.",
    stats: [
      { num: "AI", label: "Capability to Process 1,000+ Employees" },
      { num: "99.9%", label: "Payroll Accuracy Rate" },
      { num: "35+", label: "States Served" },
      { num: "Zero", label: "IRS Penalties for Clients" }
    ],
    includes: [
      [
        "Weekly, Bi-Weekly & Monthly Payroll",
        "Direct Deposit Processing",
        "Payroll Tax Withholding & Filing",
        "W-2 & 1099 Preparation",
        "New Hire Reporting & Onboarding"
      ],
      [
        "Workers' Compensation Reporting",
        "Multi-State Payroll Compliance",
        "Benefits & Deductions Management",
        "Garnishment Processing",
        "Payroll Analytics & Reporting"
      ]
    ],
    benefits: [
      { icon: <FaCheck size={28}/>, title: "Guaranteed Accuracy", desc: "Our rigorous multi-step review process virtually eliminates payroll errors that can damage employee trust and trigger IRS penalties.", blue: true },
      { icon: <FaShieldAlt size={28}/>, title: "Full Tax Compliance", desc: "All federal, state, and local payroll taxes filed and deposited on time — we track every deadline so you never miss one.", blue: false },
      { icon: <FaDollarSign size={28}/>, title: "Cost-Effective Outsourcing", desc: "Save time and eliminate overhead costs of in-house payroll while getting expert-level accuracy and speed.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Employee Satisfaction", desc: "Consistent, accurate, on-time paychecks build trust, morale, and retention within your organization.", blue: false }
    ],
    /* Fresh Idea: Payroll Processing Timeline */
    freshSection: {
      type: "payroll-flow",
      title: "How Our Payroll Process Works",
      subtitle: "A streamlined 4-step process that keeps your team paid accurately and on time — every cycle.",
      steps: [
        { num: "1", icon: "📋", label: "Submit Hours & Data", desc: "You submit employee hours, changes, and updates through our secure portal by your cutoff time." },
        { num: "2", icon: "⚙️", label: "Process & Calculate", desc: "We calculate gross pay, all deductions, withholdings, and net pay with multi-layer accuracy checks." },
        { num: "3", icon: "✅", label: "Review & Approve", desc: "You receive a pre-payroll report for your review and approval before any funds are moved." },
        { num: "4", icon: "💰", label: "Pay & File", desc: "Direct deposits are processed, tax deposits are made, and all compliance reports are filed." }
      ]
    },
    guarantee: {
      heading: "Our Payroll Accuracy Guarantee",
      text: "If a payroll error is made on our end, we will correct it immediately and cover any associated penalties or interest charges — at no cost to you. That's our commitment to 100% accuracy."
    },
    industries: [
      { icon: "🏥", label: "Healthcare" }, { icon: "🏗️", label: "Construction" },
      { icon: "🍽️", label: "Restaurants" }, { icon: "🏪", label: "Retail" },
      { icon: "💻", label: "Tech Companies" }, { icon: "🏠", label: "Real Estate" },
      { icon: "🏫", label: "Non-Profits" }, { icon: "⚖️", label: "Professional Services" }
    ],
    faqs: [
      { q: "How quickly can you process our first payroll?", a: "We can typically onboard new clients and process their first payroll within 3–5 business days. Our team handles all data migration, employee setup, tax ID registration, and bank account verification to ensure a smooth and fast transition." },
      { q: "Do you handle payroll taxes and filings automatically?", a: "Yes. We calculate, deposit, and file all federal (941, 940), state, and local payroll taxes on your behalf. We also prepare and file year-end W-2s and 1099s. You never have to worry about a payroll tax deadline again." },
      { q: "Can you manage payroll for employees in multiple states?", a: "Absolutely. Multi-state payroll is one of our specialties. We handle state income tax withholding, unemployment insurance registrations, and local tax compliance for employees working in different states, including remote workers." },
      { q: "What happens if there's a payroll error?", a: "Our accuracy guarantee means we correct any error made on our end immediately and cover any resulting IRS or state penalties. In practice, our multi-step verification process makes errors extremely rare — less than 0.1% of all payrolls processed." }
    ]
  },

  /* ---- FINANCIAL REPORTING & AUDIT (merged) ---- */
  "financial-reporting-audit": {
    title: "Financial Reporting & Audit",
    heading: "Comprehensive Financial Reporting & Compliance Audit Services",
    intro: "United Taxpert delivers a fully integrated financial reporting and compliance audit service — combining precise GAAP-compliant statements with thorough internal and external audit capabilities. We give your business the financial clarity, regulatory confidence, and audit-readiness it needs to operate with integrity and attract investors, lenders, and partners.",
    stats: [
      { num: "GAAP", label: "Compliant Financial Reports" },
      { num: "100%", label: "Audit-Ready Financials" },
      { num: "Zero", label: "Regulatory Penalties" },
      { num: "25+", label: "Years of Reporting & Audit Expertise" }
    ],
    includes: [
      ["Income Statement (Profit & Loss)", "Balance Sheet Preparation", "Cash Flow Statements", "Statement of Equity", "Monthly, Quarterly & Annual Reporting", "GAAP-Compliant Financial Statements"],
      ["Internal Control Review & Testing", "Tax Compliance Verification", "Risk Assessment & Mitigation", "IRS Audit Representation", "Fraud Detection & Prevention", "Compliance Gap Analysis & Remediation"]
    ],
    benefits: [
      { icon: <FaChartLine size={28}/>, title: "Crystal-Clear Financial Visibility", desc: "Accurate, timely financial statements give you and your stakeholders a complete picture of your company's financial health at all times.", blue: true },
      { icon: <FaShieldAlt size={28}/>, title: "GAAP & Regulatory Compliance", desc: "All reports prepared in full accordance with GAAP standards, meeting requirements for banks, investors, and regulatory bodies.", blue: false },
      { icon: <FaDollarSign size={28}/>, title: "Penalty-Free Operations", desc: "Proactive compliance auditing identifies and resolves gaps before they result in costly IRS penalties, fines, or legal exposure.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Investor & Lender Ready", desc: "Professional, audit-grade financials build the trust and credibility needed to secure financing, attract investors, and drive growth.", blue: false }
    ],
    freshSection: {
      type: "audit-checklist",
      title: "Audit Readiness Checklist",
      subtitle: "United Taxpert ensures your business is always prepared — not just at audit time, but year-round.",
      items: [
        { icon: "✅", label: "Chart of Accounts Organized", desc: "Clean, consistent COA structure that aligns with your industry and reporting requirements." },
        { icon: "✅", label: "Bank Accounts Reconciled Monthly", desc: "Every bank, credit card, and loan account reconciled within 30 days of statement close." },
        { icon: "✅", label: "Supporting Documentation Filed", desc: "All invoices, receipts, contracts, and payroll records properly filed and retrievable." },
        { icon: "✅", label: "Internal Controls Documented", desc: "Segregation of duties and internal controls documented and tested regularly." },
        { icon: "✅", label: "Prior Year Comparatives Ready", desc: "3-year comparative financial data prepared for lender, investor, or IRS review." },
        { icon: "✅", label: "Tax Obligations Current", desc: "All payroll taxes, estimated taxes, and sales taxes filed and paid on time." }
      ]
    },
    reportingTypes: [
      { grade: "Monthly", label: "Management Reports", desc: "P&L, cash flow, and KPI dashboards for day-to-day business decisions.", color: "blue" },
      { grade: "Quarterly", label: "Compliance Review", desc: "Internal control testing, tax compliance check, and variance analysis.", color: "mid" },
      { grade: "Annual", label: "Full Financial Statements", desc: "Audited or reviewed financials for lenders, investors, and tax filing.", color: "light" }
    ],
    industries: [
      { icon: "🏢", label: "Corporations" }, { icon: "🏗️", label: "Construction" },
      { icon: "🏥", label: "Healthcare" }, { icon: "🏫", label: "Non-Profits" },
      { icon: "💻", label: "Technology" }, { icon: "🏠", label: "Real Estate" },
      { icon: "⚖️", label: "Legal & Professional" }, { icon: "🏦", label: "Financial Services" }
    ],
    faqs: [
      { q: "What is the difference between a financial review and a full audit?", a: "A financial review provides limited assurance through analytical procedures and inquiries. A full audit provides the highest level of assurance through independent testing, confirmation of balances, and examination of internal controls. We help you determine the right level for your lenders, investors, or regulatory requirements." },
      { q: "How often should financial statements be prepared?", a: "We recommend monthly management reports for internal decision-making, quarterly statements for lender compliance and investor updates, and annual audited or reviewed statements for significant financing events. More frequent reporting gives you tighter control over your financial performance." },
      { q: "What does a compliance audit include?", a: "Our compliance audits cover a comprehensive review of financial records, tax filings, payroll compliance, internal controls, regulatory adherence, and documentation practices. We identify gaps, assess risk levels, and provide a detailed remediation plan with prioritized action items — and IRS audit representation if needed." },
      { q: "Can you help us prepare for a bank or investor audit?", a: "Absolutely. We specialize in preparing businesses for third-party financial scrutiny — cleaning books, organizing documentation, preparing comparative statements, and briefing your leadership. We have helped clients secure millions in financing through audit-ready financial preparation." }
    ]
  },

  /* ---- FRACTIONAL CFO SERVICES ---- */
  "fractional-cfo": {
    title: "Fractional CFO Services",
    heading: "Strategic CFO Leadership — On Your Terms",
    intro: "United Taxpert's Fractional CFO service gives your business access to executive-level financial leadership without the cost and commitment of a full-time hire. Our seasoned CFO professionals bring big-picture financial strategy, operational efficiency, investor-grade reporting, and forward-looking guidance — working alongside your team as a trusted financial partner on a flexible, scalable basis.",
    stats: [
      { num: "70%", label: "Cost Savings vs. Full-Time CFO" },
      { num: "Strategic", label: "Financial Leadership On-Demand" },
      { num: "25+", label: "Years CFO-Level Experience" },
      { num: "Growth", label: "Focused Financial Roadmap" }
    ],
    includes: [
      ["Financial Strategy & Business Planning", "Cash Flow Forecasting & Management", "Budget Development & Monitoring", "KPI Development & Performance Tracking", "Board & Investor Reporting", "Capital Structure & Funding Strategy"],
      ["M&A Advisory & Due Diligence Support", "Financial Systems & Process Optimization", "Risk Management & Internal Controls", "Lender & Investor Relations", "Exit Planning & Business Valuation", "Monthly CFO Advisory Calls & Reports"]
    ],
    benefits: [
      { icon: <FaChartLine size={28}/>, title: "Executive-Level Strategy", desc: "Gain the strategic financial insights of a seasoned CFO — driving decisions that accelerate growth and protect profitability.", blue: true },
      { icon: <FaDollarSign size={28}/>, title: "Significant Cost Savings", desc: "A full-time CFO costs $250K–$400K+ annually. Our fractional model delivers the same strategic value at a fraction of the investment.", blue: false },
      { icon: <FaShieldAlt size={28}/>, title: "Risk & Control Management", desc: "Robust financial controls, compliance oversight, and risk frameworks that protect your business as it scales and evolves.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Flexible & Scalable", desc: "Scale your CFO support up or down based on your business stage — from monthly advisory to intensive engagement during growth phases.", blue: false }
    ],
    freshSection: {
      type: "cfo-comparison",
      title: "Fractional CFO vs. Full-Time CFO",
      subtitle: "The strategic value of a world-class CFO — without the full-time price tag.",
      left: {
        heading: "❌ Full-Time CFO",
        style: "reactive",
        points: ["$250,000 – $400,000+ annual salary", "Benefits, equity & executive perks", "12–18 month hiring timeline", "Single perspective & experience", "Fixed cost regardless of workload", "Long notice period if not the right fit"]
      },
      right: {
        heading: "✅ United Taxpert Fractional CFO",
        style: "proactive",
        points: ["Fraction of full-time cost — flexible pricing", "No benefits, equity, or overhead", "Start in days, not months", "Access to a full team of financial experts", "Scale up/down as your business needs change", "Risk-free, results-driven engagement"]
      }
    },
    cfoServices: [
      { icon: "📊", title: "Financial Dashboard & KPIs", desc: "Custom executive dashboards with real-time visibility into the metrics that drive your business performance." },
      { icon: "💵", title: "Cash Flow Command Center", desc: "13-week rolling cash flow forecasts, working capital optimization, and proactive cash management strategies." },
      { icon: "🏦", title: "Lender & Investor Relations", desc: "Financial packages, pitch deck financials, and projections that help you secure debt financing and equity investment." },
      { icon: "🚀", title: "Growth & Scaling Strategy", desc: "Financial modeling for new market entry, product launches, acquisitions, and geographic expansion decisions." }
    ],
    industries: [
      { icon: "🚀", label: "Startups & Scale-ups" }, { icon: "💻", label: "Technology" },
      { icon: "🏥", label: "Healthcare" }, { icon: "🏗️", label: "Construction" },
      { icon: "🏠", label: "Real Estate" }, { icon: "🏭", label: "Manufacturing" },
      { icon: "🛒", label: "eCommerce" }, { icon: "⚖️", label: "Professional Services" }
    ],
    faqs: [
      { q: "How many hours per month does a Fractional CFO typically work?", a: "Engagement hours vary based on your needs. Early-stage companies typically need 10–20 hours per month. Companies preparing for funding rounds or M&A may require 20–40 hours. We structure each engagement around your specific goals and budget, and you can scale hours up or down as your needs change." },
      { q: "At what stage should a business consider a Fractional CFO?", a: "Most businesses benefit from fractional CFO services once they reach $1M+ in annual revenue and face increasing financial complexity — multiple revenue streams, business loans, growth planning, or M&A. Even earlier-stage businesses use fractional CFOs to build sound financial foundations and investor-ready reporting." },
      { q: "What is the difference between a CFO and a CPA or bookkeeper?", a: "A bookkeeper records transactions. A CPA ensures tax compliance and prepares reports. A CFO transforms that data into forward-looking strategy — cash flow forecasting, capital allocation, growth planning, and investor relations. Our Fractional CFO service bridges all three levels, from transaction recording to boardroom strategy." },
      { q: "Can a Fractional CFO help us raise capital or prepare for a sale?", a: "Yes — this is one of the highest-value applications. We prepare investor-grade financial models, pitch deck financials, data room documentation, and manage the financial due diligence process for equity raises and business sales. Our M&A and capital markets experience gives you a significant advantage in these high-stakes transactions." }
    ]
  },

  /* ---- INVENTORY MANAGEMENT ---- */
  "inventory-management": {
    title: "Inventory Management",
    heading: "Smart Inventory Control & Financial Optimization",
    intro: "Our inventory management services give your business real-time visibility into your stock levels, costs, and movement. United Taxpert integrates inventory controls directly with your bookkeeping to ensure accurate COGS reporting, optimized cash flow, and financial statements you can trust — giving you the data you need to make smarter purchasing and pricing decisions.",
    stats: [
      { num: "30%", label: "Avg. Inventory Cost Reduction" },
      { num: "Real-Time", label: "Stock Visibility" },
      { num: "100%", label: "COGS Accuracy" },
      { num: "Multi-Location", label: "Tracking Capability" }
    ],
    includes: [
      [
        "Real-Time Inventory Tracking",
        "Stock Level Monitoring & Alerts",
        "Cost of Goods Sold (COGS) Analysis",
        "Purchase Order Management",
        "Inventory Valuation (FIFO / LIFO / WAC)"
      ],
      [
        "Shrinkage & Variance Reporting",
        "Multi-Location Inventory Tracking",
        "Dead Stock Identification",
        "Supplier Performance Tracking",
        "Inventory Reconciliation & Audit"
      ]
    ],
    benefits: [
      { icon: <FaChartLine size={28}/>, title: "Financial Accuracy", desc: "Accurate inventory data directly improves the reliability of your financial statements, COGS, and profit margins.", blue: true },
      { icon: <FaShieldAlt size={28}/>, title: "Loss Prevention", desc: "Identify shrinkage, theft, and waste before they significantly impact your profitability and bottom line.", blue: false },
      { icon: <FaDollarSign size={28}/>, title: "Cash Flow Optimization", desc: "Smart inventory levels free up working capital and prevent costly over-stocking or revenue-killing stockouts.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Operational Efficiency", desc: "Streamlined processes and integrated systems reduce manual effort and keep your team focused on growth.", blue: false }
    ],
    /* Fresh Idea: Inventory Health Dashboard */
    freshSection: {
      type: "inventory-health",
      title: "Inventory Health Metrics We Track For You",
      subtitle: "We monitor the metrics that matter most — giving you a complete financial picture of your inventory performance.",
      metrics: [
        { icon: "🔄", label: "Inventory Turnover Ratio", desc: "How fast your stock sells. Low turnover = capital tied up. We identify slow movers fast.", target: "Industry Benchmark: 4–8x/year" },
        { icon: "📉", label: "Shrinkage Rate", desc: "Tracks loss from theft, damage, and error. Industry avg is 1.4% — we help you beat it.", target: "Goal: < 1%" },
        { icon: "💰", label: "COGS Accuracy", desc: "Ensures your Cost of Goods Sold is precisely calculated using the right valuation method for your business.", target: "Method: FIFO / LIFO / WAC" },
        { icon: "📦", label: "Reorder Point Analysis", desc: "We calculate the exact reorder points for each SKU so you never run out or overstock.", target: "Result: Zero Stockouts" },
        { icon: "🏚️", label: "Dead Stock Detection", desc: "Identify items that haven't moved in 90+ days so you can liquidate or repurpose capital.", target: "Review: Quarterly" },
        { icon: "🤝", label: "Supplier Performance", desc: "Track lead times, fulfillment rates, and pricing trends per vendor to optimize your supply chain.", target: "Goal: 98%+ On-Time" }
      ]
    },
    abcAnalysis: {
      heading: "ABC Inventory Analysis",
      desc: "We categorize your inventory into three tiers — so you focus resources where they matter most:",
      tiers: [
        { grade: "A", label: "High Value Items", desc: "Top 10% of SKUs driving 70% of revenue. Tight controls & priority tracking.", color: "blue" },
        { grade: "B", label: "Mid Value Items", desc: "Next 30% of SKUs. Balanced monitoring with regular review cycles.", color: "mid" },
        { grade: "C", label: "Low Value Items", desc: "Bottom 60% of SKUs. Efficient bulk management and periodic audits.", color: "light" }
      ]
    },
    industries: [
      { icon: "🛒", label: "eCommerce" }, { icon: "🏪", label: "Retail" },
      { icon: "🏭", label: "Manufacturing" }, { icon: "🍽️", label: "Food & Beverage" },
      { icon: "💊", label: "Healthcare" }, { icon: "🏗️", label: "Distribution" },
      { icon: "🚗", label: "Automotive" }, { icon: "👗", label: "Apparel" }
    ],
    faqs: [
      { q: "What accounting software do you integrate with for inventory?", a: "We work seamlessly with QuickBooks Online, QuickBooks Desktop, Xero, Sage, NetSuite, and many industry-specific platforms. We can also connect with POS systems like Square, Shopify, and Lightspeed to pull real-time sales data directly into your inventory records." },
      { q: "What is the difference between FIFO and LIFO inventory valuation?", a: "FIFO (First In, First Out) assumes the oldest items are sold first — ideal for most businesses and required for international reporting. LIFO (Last In, First Out) assumes newest items sell first, which can reduce taxable income during inflation but is not accepted under IFRS. We help you select the method that optimizes your tax position and financial reporting." },
      { q: "How do you handle inventory for businesses with multiple locations?", a: "We implement multi-location inventory tracking systems that give you consolidated visibility across all warehouses, stores, or facilities. Each location's stock is tracked independently and rolled up into a unified financial report, giving you both granular and big-picture insight." },
      { q: "Can inventory management help reduce my taxes?", a: "Yes. The right inventory valuation method directly impacts your Cost of Goods Sold and therefore your taxable income. Additionally, properly identifying and writing off obsolete or damaged inventory can generate valuable tax deductions. We align your inventory accounting with your overall tax strategy." }
    ]
  },

  /* ---- ADMINISTRATIVE SERVICES (International Accounting) ---- */
  "administrative-services": {
    title: "Administrative Services",
    heading: "Comprehensive Business & International Accounting Services",
    intro: "United Taxpert delivers end-to-end administrative and international accounting services designed to simplify business formation, regulatory compliance, and cross-border financial management. From forming your LLC or S-Corp to managing foreign subsidiaries and securing trademarks, our experienced team provides the expertise, precision, and strategic guidance you need to launch, protect, and grow your business seamlessly — domestically and internationally.",
    stats: [
      { num: "All 50", label: "States Covered" },
      { num: "100%", label: "Regulatory Compliance" },
      { num: "35+", label: "Countries Served" },
      { num: "25+", label: "Years Expertise" }
    ],
    includes: [
      [
        "LLC & S-Corp Formation",
        "Registered Agent Services (All States)",
        "Trademark Filing & IP Protection",
        "International Accounting Services",
        "Global Financial Reporting"
      ],
      [
        "Tax Planning and Compliance",
        "Cross-Border Transactions",
        "International Payroll Services",
        "Foreign Entity Setup and Compliance",
        "Transfer Pricing Services"
      ]
    ],
    benefits: [
      { icon: <FaChartLine size={28}/>, title: "Expertise and Knowledge", desc: "Our team brings deep international accounting expertise, GAAP and IFRS proficiency, and in-depth knowledge of global tax treaties and regulations.", blue: true },
      { icon: <FaShieldAlt size={28}/>, title: "Compliance and Risk Management", desc: "Proactive regulatory compliance monitoring, risk mitigation strategies, and comprehensive audit-ready documentation protect your global operations.", blue: false },
      { icon: <FaDollarSign size={28}/>, title: "Cost Savings and Efficiency", desc: "Streamlined processes, optimized tax structures, and efficient multi-country reporting save time, reduce overhead, and maximize profitability.", blue: false },
      { icon: <FaUsers size={28}/>, title: "Global Reach and Local Expertise", desc: "We combine worldwide reach with boots-on-the-ground local knowledge, giving you the best of both worlds for international expansion.", blue: false }
    ],
    /* Fresh Idea: Global Expansion Roadmap */
    freshSection: {
      type: "expansion-roadmap",
      title: "Your International Expansion Roadmap",
      subtitle: "A proven 3-step framework that takes you from local operations to global success — with complete financial and regulatory confidence.",
      steps: [
        { num: "1", icon: "🗓️", label: "Consultation and Assessment", desc: "Schedule a meeting with our international experts to discuss your global expansion goals, financial challenges, and current cross-border complexities. We conduct a thorough assessment of your financial infrastructure, entity structure, and regulatory exposure." },
        { num: "2", icon: "🎯", label: "Customized Strategy Development", desc: "Our team creates a tailored international accounting plan and strategy designed to address your specific multi-country needs, tax optimization opportunities, and compliance requirements. We outline clear steps, timelines, and solutions to achieve your global financial objectives." },
        { num: "3", icon: "🚀", label: "Implementation and Support", desc: "We implement the agreed-upon international accounting strategies, providing continuous monitoring, real-time adjustments, and proactive regulatory updates as needed. Ongoing support and quarterly reviews ensure you stay on track for sustained global financial success." }
      ]
    },
    businessFormation: [
      { 
        icon: "🏢", 
        title: "LLC & S-Corp Formation", 
        desc: "Complete business entity formation services in all 50 states. We handle everything from name reservation and Articles of Organization/Incorporation filing to EIN registration and operating agreement drafting. Whether you're forming an LLC for liability protection or an S-Corp for tax advantages, we ensure proper structure, compliance, and a smooth launch." 
      },
      { 
        icon: "📋", 
        title: "Registered Agent Services (All States)", 
        desc: "Professional registered agent services available in every US state. We serve as your official point of contact for legal documents, compliance notices, and government correspondence — ensuring you never miss critical deadlines. Our registered agent service includes secure document management, instant notifications, and full privacy protection for your business address." 
      },
      { 
        icon: "™️", 
        title: "Trademark Filing & IP Protection", 
        desc: "Comprehensive trademark search, registration, and intellectual property protection services. We conduct thorough clearance searches, prepare and file applications with the USPTO, respond to office actions, and monitor your trademark portfolio. Protect your brand name, logo, and business identity with expert trademark counsel and proactive IP strategy." 
      },
      { 
        icon: "🌐", 
        title: "International Accounting Services", 
        desc: "Full-scale international accounting support for businesses operating across borders. From multi-currency financial reporting and GAAP/IFRS compliance to transfer pricing, foreign entity setup, and cross-border tax planning — we deliver the specialized expertise you need to manage global operations with confidence, clarity, and complete regulatory compliance." 
      }
    ],
    globalServices: [
      { icon: "🌍", title: "Multi-Country Financial Consolidation", desc: "Unified financial reporting across all international entities with automated currency conversion and intercompany elimination." },
      { icon: "🔄", title: "Transfer Pricing Compliance", desc: "Arm's length analysis, transfer pricing documentation, and proactive defense against international tax authority challenges." },
      { icon: "💼", title: "Foreign Entity Formation", desc: "Complete support for establishing subsidiaries, branches, or joint ventures abroad — from registration to banking setup." },
      { icon: "📊", title: "International Tax Treaty Optimization", desc: "Leverage bilateral tax treaties to minimize withholding taxes, avoid double taxation, and maximize after-tax returns." }
    ],
    industries: [
      { icon: "💻", label: "Technology & SaaS" }, { icon: "🏭", label: "Manufacturing" },
      { icon: "🛒", label: "eCommerce" }, { icon: "⚖️", label: "Professional Services" },
      { icon: "🏗️", label: "Construction" }, { icon: "🏥", label: "Healthcare" },
      { icon: "🏦", label: "Financial Services" }, { icon: "🚀", label: "Startups Expanding Globally" }
    ],
    faqs: [
      { q: "What is the difference between GAAP and IFRS reporting?", a: "GAAP (Generally Accepted Accounting Principles) is the US accounting standard, while IFRS (International Financial Reporting Standards) is used in over 140 countries worldwide. For businesses operating internationally, we prepare financials under both frameworks to meet the requirements of all stakeholders — US investors, foreign regulators, and international lenders." },
      { q: "How do you handle multi-currency transactions and reporting?", a: "We use advanced accounting systems to track transactions in their native currencies, apply real-time or period-end exchange rates, and consolidate everything into your primary reporting currency. We also handle foreign exchange gains/losses, hedging transactions, and multi-currency cash flow forecasting." },
      { q: "What is transfer pricing and why does it matter?", a: "Transfer pricing governs how related entities (parent companies and foreign subsidiaries) price transactions between each other. Tax authorities scrutinize these prices to prevent profit shifting to low-tax jurisdictions. We document arm's length pricing, perform benchmarking studies, and prepare contemporaneous documentation to defend your transfer pricing policies during audits." },
      { q: "Can you help us establish a foreign subsidiary or branch office?", a: "Yes. We guide you through the entire process — from selecting the optimal entity structure and jurisdiction to registering with local authorities, opening bank accounts, obtaining tax IDs, and setting up compliant payroll and accounting systems. We work alongside your legal counsel to ensure a seamless international expansion." }
    ]
  }
};

/* ==========================================
   PROCESS STEPS (PER SERVICE)
   ========================================== */
const processStepsData = {
  "business-acquisitions": [
    { num: "01", title: "Discovery & Target Identification", desc: "We assess your acquisition goals, budget, and strategic fit criteria to identify the right targets.", points: ["Define acquisition criteria — size, industry, geography, valuation range.", "Review your financial readiness and structure of the proposed deal."], dark: true },
    { num: "02", title: "Due Diligence & Valuation", desc: "Deep-dive financial review and independent valuation to uncover risks and confirm fair value.", points: ["Comprehensive review of 3–5 years of financials, taxes, contracts, and liabilities.", "Independent business valuation using DCF, market comparables, and asset-based methods."], dark: false },
    { num: "03", title: "Closing & Integration", desc: "We manage the close and provide a structured 90-day post-merger integration plan.", points: ["Coordinate with legal team on purchase agreement, escrow, and conditions to close.", "90-day post-merger integration: financial systems, payroll, reporting unification."], dark: false }
  ],
  "tax-planning": [
    { num: "01", title: "Financial Assessment & Discovery", desc: "We review your complete financial picture — income, deductions, entity structure, and prior returns.", points: ["Analyze prior 2–3 years of tax returns to identify missed opportunities.", "Evaluate your current entity structure for optimal tax efficiency."], dark: true },
    { num: "02", title: "Tax Strategy Development", desc: "We build a personalized, multi-year tax minimization roadmap tailored to your goals.", points: ["Identify all applicable deductions, credits, and timing strategies.", "Design a quarterly tax calendar with estimated payment schedules."], dark: false },
    { num: "03", title: "Implementation & Year-Round Monitoring", desc: "We implement your strategy and monitor throughout the year as tax laws and your finances evolve.", points: ["Quarterly review calls to adjust strategy as your business grows or changes.", "Proactive alerts when new tax legislation impacts your situation."], dark: false }
  ],
  "payroll-services": [
    { num: "01", title: "Onboarding & Setup", desc: "We migrate all employee data, set up tax accounts, and configure your payroll schedule.", points: ["Complete employee data collection — W-4s, direct deposit, benefits, garnishments.", "Register all state and local payroll tax accounts on your behalf."], dark: true },
    { num: "02", title: "Payroll Processing", desc: "Each cycle, we calculate pay, run accuracy checks, and prepare your pre-payroll report.", points: ["Multi-layer verification: hours, rates, deductions, and tax withholdings reviewed.", "Pre-payroll report sent for your approval before any funds are disbursed."], dark: false },
    { num: "03", title: "Tax Filing & Reporting", desc: "We handle all tax deposits, quarterly filings, and year-end W-2 and 1099 preparation.", points: ["Automatic federal and state payroll tax deposits — never miss a deadline.", "Year-end W-2/1099 filing with employee and contractor distribution."], dark: false }
  ],
  "financial-reporting-audit": [
    { num: "01", title: "Financial Assessment & Clean-Up", desc: "We review your existing books, identify inconsistencies, and establish clean opening balances.", points: ["Comprehensive review of chart of accounts, reconciliations, and prior period entries.", "Identify and correct errors, omissions, and classification issues."], dark: true },
    { num: "02", title: "Reporting Setup & Audit Framework", desc: "We build your reporting structure and compliance framework tailored to your business needs.", points: ["Design GAAP-compliant financial statement formats for monthly, quarterly, and annual reporting.", "Establish internal control documentation and compliance calendar."], dark: false },
    { num: "03", title: "Ongoing Reporting & Audit Review", desc: "Monthly reporting delivery with quarterly compliance reviews and annual audit preparation.", points: ["Monthly financial package delivered within 10 business days of month-end close.", "Quarterly internal control testing and annual audit-ready financial preparation."], dark: false }
  ],
  "fractional-cfo": [
    { num: "01", title: "CFO Discovery & Financial Assessment", desc: "We conduct a deep-dive into your current financial position, systems, and strategic goals.", points: ["Review financials, cash flow, debt structure, and current reporting capabilities.", "Identify immediate financial risks and high-priority strategic opportunities."], dark: true },
    { num: "02", title: "CFO Strategy & Roadmap Development", desc: "We build a 12-month financial roadmap aligned with your growth objectives.", points: ["Develop KPI framework, budget model, and cash flow forecasting system.", "Establish board reporting format and investor/lender communication strategy."], dark: false },
    { num: "03", title: "Ongoing CFO Advisory & Execution", desc: "Monthly advisory calls, financial reporting, and strategic decision support.", points: ["Monthly CFO report including financial statements, KPIs, variance analysis, and recommendations.", "On-call advisory support for financing decisions, M&A, pricing, and growth strategy."], dark: false }
  ],
  "inventory-management": [
    { num: "01", title: "Inventory Audit & Setup", desc: "We begin with a full inventory audit to establish accurate baseline counts and valuations.", points: ["Physical or system-based inventory count to establish opening balances.", "Select and configure the optimal valuation method (FIFO, LIFO, or WAC)."], dark: true },
    { num: "02", title: "System Integration & Tracking", desc: "We connect your inventory data with your accounting system for real-time financial visibility.", points: ["Integrate with your POS, e-commerce, or warehouse management system.", "Configure automated low-stock alerts, reorder points, and reporting dashboards."], dark: false },
    { num: "03", title: "Ongoing Monitoring & Optimization", desc: "Monthly reconciliations, variance analysis, and COGS reporting to keep your books accurate.", points: ["Monthly inventory reconciliation with financial statements.", "Quarterly ABC analysis and shrinkage review to optimize your product mix."], dark: false }
  ],
  "administrative-services": [
    { num: "01", title: "Consultation and Assessment", desc: "We begin with a comprehensive discovery session to understand your international operations and expansion goals.", points: ["Schedule a meeting with our international accounting experts to discuss your financial challenges, cross-border transactions, and global growth objectives.", "We conduct a thorough assessment of your current financial infrastructure, entity structure, multi-currency operations, and regulatory compliance exposure."], dark: true },
    { num: "02", title: "Customized Strategy Development", desc: "Our team creates a tailored international accounting plan aligned with your specific multi-country needs.", points: ["Develop a customized financial strategy covering entity structuring, tax optimization, transfer pricing, and regulatory compliance roadmap.", "We outline clear implementation steps, timelines, deliverables, and solutions to achieve your global financial objectives with maximum efficiency."], dark: false },
    { num: "03", title: "Implementation and Support", desc: "We execute your international accounting strategy with continuous monitoring and proactive updates.", points: ["Implement all agreed-upon strategies including multi-country reporting, payroll setup, tax filings, and compliance systems — with real-time adjustments as regulations evolve.", "Ongoing quarterly strategy reviews, regulatory monitoring, and dedicated support ensure you maintain global financial success and compliance year-round."], dark: false }
  ]
};

/* ==========================================
   FRESH SECTION RENDERERS
   ========================================== */

const DealTimeline = ({ data }) => (
  <div className="fresh-section deal-timeline">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="timeline-steps">
      {data.steps.map((step, i) => (
        <div key={i} className="timeline-step">
          <div className="timeline-step-icon">{step.icon}</div>
          <div className="timeline-connector" />
          <h4>{step.label}</h4>
          <p>{step.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const RiskSection = ({ risks }) => (
  <div className="risk-section">
    <h3>Why Acquisitions Fail — And How We Prevent It</h3>
    <p className="fresh-subtitle">73% of mergers and acquisitions fail to create value. Our structured approach directly addresses the four most common reasons.</p>
    <div className="risk-grid">
      {risks.map((r, i) => (
        <div key={i} className="risk-card">
          <div className="risk-icon">{r.icon}</div>
          <h4>{r.title}</h4>
          <p>{r.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const ComparisonSection = ({ data }) => (
  <div className="fresh-section comparison-section">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="comparison-grid">
      <div className="comparison-col reactive">
        <div className="comparison-header reactive-header">{data.left.heading}</div>
        <ul>{data.left.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
      </div>
      <div className="comparison-divider"><span>VS</span></div>
      <div className="comparison-col proactive">
        <div className="comparison-header proactive-header">{data.right.heading}</div>
        <ul>{data.right.points.map((p, i) => <li key={i}>{p}</li>)}</ul>
      </div>
    </div>
  </div>
);

const TaxCalendar = ({ dates }) => (
  <div className="tax-calendar">
    <h3>Key Tax Deadlines — We Track Every One</h3>
    <div className="calendar-grid">
      {dates.map((d, i) => (
        <div key={i} className="calendar-item">
          <div className="calendar-date">{d.date}</div>
          <div className="calendar-desc">{d.desc}</div>
        </div>
      ))}
    </div>
  </div>
);

const PayrollFlow = ({ data }) => (
  <div className="fresh-section payroll-flow">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="payroll-steps">
      {data.steps.map((step, i) => (
        <div key={i} className="payroll-step">
          <div className="payroll-step-emoji">{step.icon}</div>
          <div className="payroll-step-num">{step.num}</div>
          <h4>{step.label}</h4>
          <p>{step.desc}</p>
          {i < data.steps.length - 1 && <div className="payroll-arrow">→</div>}
        </div>
      ))}
    </div>
  </div>
);

const GuaranteeBadge = ({ data }) => (
  <div className="guarantee-badge">
    <div className="guarantee-icon">🛡️</div>
    <div className="guarantee-text">
      <h4>{data.heading}</h4>
      <p>{data.text}</p>
    </div>
  </div>
);

const InventoryHealth = ({ data }) => (
  <div className="fresh-section inventory-health">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="metrics-grid">
      {data.metrics.map((m, i) => (
        <div key={i} className="metric-card">
          <div className="metric-icon">{m.icon}</div>
          <h4>{m.label}</h4>
          <p>{m.desc}</p>
          <div className="metric-target">{m.target}</div>
        </div>
      ))}
    </div>
  </div>
);

const ABCAnalysis = ({ data }) => (
  <div className="abc-analysis">
    <h3>{data.heading}</h3>
    <p className="fresh-subtitle">{data.desc}</p>
    <div className="abc-grid">
      {data.tiers.map((t, i) => (
        <div key={i} className={`abc-tier ${t.color}`}>
          <div className="abc-grade">{t.grade}</div>
          <h4>{t.label}</h4>
          <p>{t.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const AuditChecklist = ({ data }) => (
  <div className="fresh-section audit-checklist">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="audit-checklist-grid">
      {data.items.map((item, i) => (
        <div key={i} className="audit-checklist-item">
          <div className="audit-check-icon">{item.icon}</div>
          <div>
            <h4>{item.label}</h4>
            <p>{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ReportingTypes = ({ types }) => (
  <div className="reporting-types">
    <h3>Our Reporting Cadence</h3>
    <p className="fresh-subtitle">We deliver the right financial information at the right time — every month, quarter, and year.</p>
    <div className="abc-grid">
      {types.map((t, i) => (
        <div key={i} className={`abc-tier ${t.color}`}>
          <div className="abc-grade" style={{ fontSize: "24px", letterSpacing: "1px" }}>{t.grade}</div>
          <h4>{t.label}</h4>
          <p>{t.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const CFOServiceCards = ({ services }) => (
  <div className="cfo-service-cards">
    <h3>What Your Fractional CFO Delivers</h3>
    <p className="fresh-subtitle">Beyond the numbers — strategic, high-impact financial leadership across four critical areas.</p>
    <div className="cfo-cards-grid">
      {services.map((s, i) => (
        <div key={i} className="cfo-card">
          <div className="cfo-card-icon">{s.icon}</div>
          <h4>{s.title}</h4>
          <p>{s.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const ExpansionRoadmap = ({ data }) => (
  <div className="fresh-section payroll-flow">
    <h3>{data.title}</h3>
    <p className="fresh-subtitle">{data.subtitle}</p>
    <div className="payroll-steps">
      {data.steps.map((step, i) => (
        <div key={i} className="payroll-step">
          <div className="payroll-step-emoji">{step.icon}</div>
          <div className="payroll-step-num">{step.num}</div>
          <h4>{step.label}</h4>
          <p>{step.desc}</p>
          {i < data.steps.length - 1 && <div className="payroll-arrow">→</div>}
        </div>
      ))}
    </div>
  </div>
);

const GlobalServiceCards = ({ services }) => (
  <div className="cfo-service-cards">
    <h3>What Makes Our International Services Stand Out</h3>
    <p className="fresh-subtitle">Comprehensive global solutions that give you competitive advantage across borders.</p>
    <div className="cfo-cards-grid">
      {services.map((s, i) => (
        <div key={i} className="cfo-card">
          <div className="cfo-card-icon">{s.icon}</div>
          <h4>{s.title}</h4>
          <p>{s.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const BusinessFormationCards = ({ services }) => (
  <div className="business-formation-section">
    <h3>Our Core Administrative Capabilities</h3>
    <p className="fresh-subtitle">From business formation to trademark protection — we handle the critical administrative foundations of your success.</p>
    <div className="formation-cards-grid">
      {services.map((s, i) => (
        <div key={i} className="formation-card">
          <div className="formation-icon">{s.icon}</div>
          <h4>{s.title}</h4>
          <p>{s.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ==========================================
   MAIN COMPONENT
   ========================================== */

const GenericServicePage = ({ serviceKey }) => {
  const data = serviceData[serviceKey];
  const steps = processStepsData[serviceKey];
  if (!data) return null;

  return (
    <div className="site-wrapper">
      <Header />
      <PageHero
        title={data.title}
        bg="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80"
      />

      {/* Stats Bar */}
      <StatsBar stats={data.stats} />

      {/* Main 2-column body */}
      <section className="svc-page-body">
        <div className="container">
          <div className="svc-page-grid">

            {/* SIDEBAR */}
            <aside className="svc-sidebar">
              <div className="svc-trouble-card">
                <h3>Having Trouble Evaluating Your Finances?</h3>
                <p>Let United Taxpert make it simple. Our financial experts help you assess your current position, uncover inefficiencies, and create a clear path toward better financial health.</p>
                <a href="mailto:refunds@unitedtaxpert.com" className="btn btn-primary btn-full">Contact Us Now</a>
              </div>
              <div className="svc-sidebar-img">
                <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&auto=format&fit=crop&q=80" alt="Advisor" />
              </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="svc-main-content">
              <h2>{data.heading}</h2>
              <p className="svc-intro">{data.intro}</p>

              <div className="svc-content-img">
                <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80" alt={data.title} />
              </div>

              {/* Service Include */}
              <div className="svc-include">
                <h3>Service Include</h3>
                <p>Our {data.title.toLowerCase()} services are comprehensive, reliable, and tailored to your unique business needs and industry requirements.</p>
                <div className="svc-include-grid">
                  {data.includes.map((col, ci) => (
                    <ul key={ci} className="svc-include-list">
                      {col.map((item, i) => (
                        <li key={i}><FaCheck className="svc-check" /> {item}</li>
                      ))}
                    </ul>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="svc-benefits">
                <h3>Benefits</h3>
                <p>Partnering with United Taxpert for {data.title.toLowerCase()} delivers measurable advantages for your business growth, compliance, and financial health.</p>
                <div className="svc-benefits-grid">
                  {data.benefits.map((b, i) => (
                    <div key={i} className={`svc-benefit-card ${b.blue ? "blue" : "white"}`}>
                      <div className="svc-benefit-icon">{b.icon}</div>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing CTA */}
              <div className="svc-pricing-cta">
                <div className="pricing-cta-text">
                  <FaChartLine size={24} />
                  <div>
                    <h4>View Our Pricing Plans</h4>
                    <p>Transparent, competitive pricing for every business size.</p>
                  </div>
                </div>
                <Link to="/pricing" className="btn btn-primary">See Pricing <FaArrowRight /></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FRESH VALUE-ADD SECTIONS ===== */}
      <section className="svc-fresh-section">
        <div className="container">

          {/* Business Acquisitions */}
          {serviceKey === "business-acquisitions" && (
            <>
              <DealTimeline data={data.freshSection} />
              <RiskSection risks={data.risks} />
            </>
          )}

          {/* Tax Planning */}
          {serviceKey === "tax-planning" && (
            <>
              <ComparisonSection data={data.freshSection} />
              <TaxCalendar dates={data.taxCalendar} />
            </>
          )}

          {/* Payroll Services */}
          {serviceKey === "payroll-services" && (
            <>
              <PayrollFlow data={data.freshSection} />
              <GuaranteeBadge data={data.guarantee} />
            </>
          )}

          {/* Inventory Management */}
          {serviceKey === "inventory-management" && (
            <>
              <InventoryHealth data={data.freshSection} />
              <ABCAnalysis data={data.abcAnalysis} />
            </>
          )}

          {/* Financial Reporting & Audit */}
          {serviceKey === "financial-reporting-audit" && (
            <>
              <AuditChecklist data={data.freshSection} />
              <ReportingTypes types={data.reportingTypes} />
            </>
          )}

          {/* Fractional CFO */}
          {serviceKey === "fractional-cfo" && (
            <>
              <ComparisonSection data={data.freshSection} />
              <CFOServiceCards services={data.cfoServices} />
            </>
          )}

          {/* Administrative Services (International Accounting) */}
          {serviceKey === "administrative-services" && (
            <>
              <BusinessFormationCards services={data.businessFormation} />
              <ExpansionRoadmap data={data.freshSection} />
              <GlobalServiceCards services={data.globalServices} />
            </>
          )}

          {/* Industries & FAQ */}
          <IndustriesRow industries={data.industries} />
          <FAQSection faqs={data.faqs} />
        </div>
      </section>

      {/* Process Steps */}
      <section className="svc-process-section">
        <div className="svc-process-overlay" />
        <div className="container">
          <div className="svc-process-grid">
            {steps.map((step, i) => (
              <div key={i} className={`svc-step-card ${step.dark ? "dark" : "light"}`}>
                <div className="svc-step-num">{step.num}</div>
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
                <ul>{step.points.map((pt, pi) => <li key={pi}><span className="step-bullet">—</span> {pt}</li>)}</ul>
              </div>
            ))}
            <div className="svc-precision-card">
              <h3>Precision in Financial <span className="text-blue">*Steps</span></h3>
              <p>We follow a proven, structured process to ensure every service is delivered with the highest level of accuracy, compliance, and strategic insight — tailored to your industry and goals.</p>
              <a href="/#contact" className="btn btn-primary">Consultation Now</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GenericServicePage;
